import { Builder } from '@builder.io/react';
import { twJoin } from 'tailwind-merge';

import CalculatorField from 'components/Calculator/components/CalculatorField';
import CalculatorResultRow from 'components/Calculator/components/CalculatorResultRow';
import { useCalculatorInputs } from 'components/Calculator/hooks/useCalculatorInputs';
import { evaluateResults } from 'components/Calculator/utils/evaluateResults';
import { findDuplicateInputKeys } from 'components/Calculator/utils/findDuplicateInputKeys';

import type { CalculatorConfigProps } from 'components/Calculator/config';
import type { CalculatorInputField, CalculatorResultField } from 'components/Calculator/config/types';
import type { FC } from 'react';

/**
 * An inline calculator for a blog post, configured entirely from Builder.
 *
 * The container follows the existing inline article callout, so a calculator
 * sits in a post the way the other inline pieces already do.
 *
 * The formula is not rendered: per Bru it is printed in the post copy directly
 * above the calculator, so repeating it here would only say the same thing
 * twice. Fields start pre-filled, so a reader meets a worked example rather
 * than an empty form, and edits from there.
 */
const Calculator: FC<CalculatorConfigProps> = ({ heading, inputs, results, supportingText }) => {
  const inputFields = (inputs ?? []) as CalculatorInputField[];
  const resultFields = (results ?? []) as CalculatorResultField[];

  const { scope, setValue, values, waitingForInput } = useCalculatorInputs(inputFields);
  const evaluated = evaluateResults(resultFields, scope);

  // Builder.isEditing is false in production on both server and client, so this
  // only ever changes what an editor sees while configuring the calculator.
  const showConfigErrors = Builder.isEditing;

  if (inputFields.length === 0 || resultFields.length === 0) {
    return showConfigErrors ? (
      <div className="max-w-[800px] rounded-lg border-[1.5px] border-dashed border-primary-300 p-6 text-md text-primary-900">
        Add at least one input and one result to this calculator.
      </div>
    ) : null;
  }

  // Duplicate input keys collapse silently: the fields share one value, so
  // typing in one moves the other and a formula can only read the last. Result
  // keys already report their own collisions, so these say so too.
  const duplicateKeys = findDuplicateInputKeys(inputFields);

  return (
    <div className="max-w-[800px] rounded-lg border-[1.5px] border-solid border-primary-300 bg-primary-50 p-6 sm:p-8">
      <div className="flex flex-col gap-1">
        {heading !== undefined && heading !== '' && (
          <h3 className="font-spaceGrotesk text-xl font-semibold text-primary-1100 sm:text-display-xs">{heading}</h3>
        )}
        {supportingText !== undefined && supportingText !== '' && (
          <p className="text-md text-primary-900">{supportingText}</p>
        )}
      </div>

      {/* Two columns once there are more than three fields. The eight-input
          Annual Cost per Integration is unreadable as a single tall column,
          and a two-field calculator looks broken split across two. */}
      <div
        className={twJoin(
          'mt-6 grid gap-x-5 gap-y-4',
          inputFields.length > 3 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1',
        )}
      >
        {inputFields.map(field => (
          <CalculatorField field={field} key={field.key} onChange={setValue} value={values[field.key] ?? ''} />
        ))}
      </div>

      {showConfigErrors && duplicateKeys.length > 0 && (
        <p className="mt-4 text-md text-error-700">
          More than one input uses the key {duplicateKeys.map(key => `"${key}"`).join(', ')}. Those fields share a
          single value, and a formula can only read the last one. Give each input its own key.
        </p>
      )}

      {/* Announced politely, so a reader using a screen reader hears the figure
          change as they type rather than having to hunt for it. */}
      <div aria-live="polite" className="mt-6 flex flex-col gap-3 border-t border-dashed border-primary-300 pt-6">
        {evaluated.map(result => (
          <CalculatorResultRow
            key={result.key}
            result={result}
            showConfigErrors={showConfigErrors}
            waitingForInput={waitingForInput}
          />
        ))}
      </div>
    </div>
  );
};

export default Calculator;
